import { useEffect, useState } from 'react';
import { Link as RouterLink, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/InsightsOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import InboxIcon from '@mui/icons-material/MarkunreadMailboxOutlined';
import HelpIcon from '@mui/icons-material/QuizOutlined';
import BuildIcon from '@mui/icons-material/TuneOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsNoneOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INSPECTOR_ROLE_CODE,
  formatDateTime,
  initial,
  type NotificationDto,
  type NotificationUnreadCount,
  type Paginated,
} from '@pmc/shared';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { PMC } from '../theme';

const SIDEBAR_WIDTH = 232;

const ADMIN_NAV = [
  { to: '/', label: 'Dashboard', icon: <DashboardIcon fontSize="small" />, end: true },
  { to: '/transactions', label: 'Transactions', icon: <PaymentsIcon fontSize="small" /> },
  { to: '/applications', label: 'Applications', icon: <AssignmentIcon fontSize="small" /> },
  { to: '/queries', label: 'Queries', icon: <InboxIcon fontSize="small" /> },
  { to: '/notifications', label: 'Notifications', icon: <NotificationsIcon fontSize="small" /> },
  { to: '/faqs', label: 'FAQs', icon: <HelpIcon fontSize="small" /> },
  { to: '/services', label: 'Service Builder', icon: <BuildIcon fontSize="small" /> },
];

/**
 * A field inspector's access starts and ends at their own assigned queue —
 * no dashboard, transactions, queries, FAQs or Service Builder (all
 * Administrator-only server-side too, see `admin.routes.ts`). Notifications
 * is available to any admin role, so it's here too.
 */
const INSPECTOR_NAV = [
  { to: '/applications', label: 'My Applications', icon: <AssignmentIcon fontSize="small" />, end: true },
  { to: '/notifications', label: 'Notifications', icon: <NotificationsIcon fontSize="small" /> },
];

/** Notification bell for the signed-in admin/inspector. */
function AdminNotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const { data: unread } = useQuery({
    queryKey: ['admin-notifications-unread'],
    queryFn: () => api.get<NotificationUnreadCount>('/admin/notifications/unread-count'),
    staleTime: 60_000,
  });

  const { data: recent } = useQuery({
    queryKey: ['admin-notifications', 'recent'],
    queryFn: () =>
      api.get<Paginated<NotificationDto>>('/admin/notifications', { page: 1, perPage: 5 }),
    staleTime: 60_000,
  });

  const items = recent?.rows ?? [];
  const badgeCount = unread?.unread ?? 0;

  const markAllRead = useMutation({
    mutationFn: () => api.post('/admin/notifications/read-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-notifications-unread'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
    },
  });

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={(e) => {
            setAnchor(e.currentTarget);
            if (badgeCount > 0) markAllRead.mutate();
          }}
          aria-label="Notifications"
        >
          <Badge badgeContent={badgeCount} color="error" max={9}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 320, maxWidth: '92vw' } } }}
      >
        <Typography variant="h6" sx={{ px: 2, py: 1 }}>
          Notifications
        </Typography>
        <Divider />
        {items.length === 0 ? (
          <MenuItem disabled sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No new notifications.
            </Typography>
          </MenuItem>
        ) : (
          items.map((item) => (
            <MenuItem
              key={item.id}
              sx={{ display: 'block', py: 1.1 }}
              onClick={() => {
                setAnchor(null);
                if (item.linkPath) navigate(item.linkPath);
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.refId ? `${item.refId} · ` : ''}
                {formatDateTime(item.createdAt)}
              </Typography>
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate('/notifications');
          }}
          sx={{ justifyContent: 'center', color: PMC.primary, fontWeight: 600 }}
        >
          View all notifications
        </MenuItem>
      </Menu>
    </>
  );
}

function timestamp(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${min}`;
}

/** Standard footer bar — copyright left, security/session status right. Shared across every admin/inspector page via this layout. */
function AppFooter() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <Box
      component="footer"
      className="no-print"
      sx={{ borderTop: `1px solid ${PMC.line}`, backgroundColor: '#fff', px: { xs: 2, md: 3 }, py: 1.25 }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography sx={{ fontSize: 12, color: PMC.muted }}>
          © {now.getFullYear()} PMC Property Tax System. Pune Municipal Corporation. All rights reserved.
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <VerifiedUserOutlinedIcon sx={{ fontSize: 14, color: PMC.muted }} />
          <Typography sx={{ fontSize: 12, color: PMC.muted }}>Secure System</Typography>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: PMC.muted }} />
          <Typography sx={{ fontSize: 12, color: PMC.muted }} className="num">
            {timestamp(now)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

export function AdminLayout() {
  const { user, signOut } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isInspector = user?.roleCode === INSPECTOR_ROLE_CODE;
  const NAV = isInspector ? INSPECTOR_NAV : ADMIN_NAV;
  const homeTo = isInspector ? '/applications' : '/';

  const nav = (
    <List sx={{ px: 1.25, py: 2 }}>
      {NAV.map((item) => {
        const selected = item.end
          ? location.pathname === item.to
          : location.pathname.startsWith(item.to);
        return (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            selected={selected}
            onClick={() => setMobileOpen(false)}
            sx={{
              borderRadius: 2,
              mb: 0.25,
              '&.Mui-selected': {
                backgroundColor: PMC.primaryLight,
                color: PMC.primary,
                '& .MuiListItemIcon-root': { color: PMC.primary },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              slotProps={{ primary: { fontSize: 13, fontWeight: selected ? 600 : 400 } }}
            />
          </ListItemButton>
        );
      })}
    </List>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ backgroundColor: PMC.navy, zIndex: 1201 }}
        className="no-print"
      >
        <Toolbar sx={{ minHeight: 58, gap: 1.5 }}>
          <IconButton
            color="inherit"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>

          <Stack component={RouterLink} to={homeTo} direction="row" spacing={1.25} alignItems="center" sx={{ textDecoration: 'none' }}>
            <Box
              component="img"
              src="/PMC_Logo.png"
              alt=""
              sx={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }}
            />
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.2 }}>
                Portal Admin
              </Typography>
              <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.7)', lineHeight: 1.2 }}>
                Property Tax Department
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ flex: 1 }} />

          <AdminNotificationBell />

          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: PMC.primary }}>
              {initial(user?.fullName)}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#fff', lineHeight: 1.2 }}>
                Welcome, {user?.fullName}
              </Typography>
              <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,.7)', lineHeight: 1.2 }}>
                {user?.roleCode}
                {user?.wardName ? ` · ${user.wardName}` : ''}
                {user?.zoneName ? `, ${user.zoneName}` : ''}
              </Typography>
            </Box>
          </Stack>

          <Button color="inherit" size="small" startIcon={<LogoutIcon />} onClick={signOut} sx={{ ml: 1 }}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1 }}>
        {isDesktop ? (
          <Box
            component="nav"
            className="no-print"
            sx={{
              width: SIDEBAR_WIDTH,
              flexShrink: 0,
              borderRight: `1px solid ${PMC.line}`,
              backgroundColor: '#fff',
            }}
          >
            {nav}
          </Box>
        ) : (
          <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}>
            <Box sx={{ width: SIDEBAR_WIDTH }}>{nav}</Box>
          </Drawer>
        )}

        <Box component="main" sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      <AppFooter />
    </Box>
  );
}
